import Button from "@app/components/button";
import Checkbox from "@app/components/checkbox";
import CustomEditor from "@app/components/custom-editor";
import CustomReactSelect from "@app/components/custom-react-select";
import FileInput from "@app/components/file-input";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import TextArea from "@app/components/textarea";
import { BUCKET_URL_KEY, MAX_SIZE_UPLOAD, SystemMessage } from "@app/constants";
import UploadMediaService from "@app/services/http/upload-media.service";
import { sendAudiobook, showAudiobookPanel } from "@app/store/audiobook";
import { BookElement, ModelTrain } from "@app/types";
import { Images } from "@assets/images";
import StorageService from "@core/services/storage";
import { FieldArray, useFormikContext } from "formik";
import { ChangeEvent, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { RiCloseLine } from "react-icons/ri";
import { EndpointConversion } from "@app/types/voice-convert.type";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SubChapter from "./sub-chapter.component";
import { openPortalDialog } from "@app/services/modal.service";
import ConfirmModal from "@app/components/confirm-modal";

const getImageDimensions = (
  file: File
): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = reject;
      img.src = e.target?.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

interface ChapterProps {
  arrayName: string;
  modelTrains?: ModelTrain[];
  endpointConversions?: EndpointConversion[];
  isUpdate?: boolean;
  canCreateAudio?: boolean;
  canUploadAudio?: boolean;
}

function Chapter({
  arrayName,
  endpointConversions,
  modelTrains,
  isUpdate,
  canCreateAudio = false,
  canUploadAudio = false,
}: ChapterProps) {
  const inputAudioChapterRef = useRef<{ [key: string]: HTMLInputElement }>({});
  const s3Url = StorageService.getObject(BUCKET_URL_KEY);

  const [expandedChapters, setExpandedChapters] = useState<{
    [key: string]: boolean;
  }>({});

  const [expandedSubChapters, setExpandedSubChapters] = useState<{
    [key: string]: boolean;
  }>({});

  const dispatch = useDispatch();
  const maxLevel = 3; // 0=sub1, 1=sub2, 2=sub3 (total 3 sub levels, cấp 4 is deepest)

  const formik = useFormikContext<{ elements: BookElement[] }>();
  const values =
    formik.values &&
    formik.values[arrayName as keyof { elements: BookElement[] }];

  const onAddAudioChapterClick = (indexParent: number, audioIndex: number) => {
    const refKey = `${indexParent}-${audioIndex}`;

    if (inputAudioChapterRef.current[refKey]) {
      inputAudioChapterRef.current[refKey].click();
    }
  };

  const handleAudioChapterChange = async (
    event: ChangeEvent<HTMLInputElement>,
    indexParent: number,
    audioIndex: number
  ) => {
    if (!event.target.files?.length) {
      formik.setFieldValue(
        `${arrayName}.${indexParent}.audios[${audioIndex}].audio`,
        undefined
      );
      formik.setFieldError(
        `${arrayName}.${indexParent}.audios[${audioIndex}].audio`,
        undefined
      );

      return;
    }

    const file = event.target.files[0];

    if (file.size > MAX_SIZE_UPLOAD) {
      formik.setFieldValue(
        `${arrayName}.${indexParent}.audios[${audioIndex}].audio`,
        undefined
      );
      formik.setFieldError(
        `${arrayName}.${indexParent}.audios[${audioIndex}].audio`,
        SystemMessage.MAX_SIZE_UPLOAD
      );

      return;
    }

    try {
      const response = await UploadMediaService.upload({ medias: [file] });
      const uploadedUrl = response.data.items[0].url;

      formik.setFieldError(
        `${arrayName}.${indexParent}.audios[${audioIndex}].audio`,
        undefined
      );
      formik.setFieldValue(
        `${arrayName}.${indexParent}.audios[${audioIndex}].audio`,
        uploadedUrl
      );
    } catch (error) {
      formik.setFieldError(
        `${arrayName}.${indexParent}.audios[${audioIndex}].audio`,
        "Error uploading file"
      );
    }
  };

  const handleAudioSubChapterChange = async (
    event: ChangeEvent<HTMLInputElement>,
    parentPath: string,
    childIndex: number,
    audioIndex: number
  ) => {
    if (!event.target.files?.length) {
      formik.setFieldValue(
        `${parentPath}.${childIndex}.audios[${audioIndex}].audio`,
        undefined
      );
      formik.setFieldError(
        `${parentPath}.${childIndex}.audios[${audioIndex}].audio`,
        undefined
      );

      return;
    }

    const file = event.target.files[0];

    if (file.size > MAX_SIZE_UPLOAD) {
      formik.setFieldValue(
        `${parentPath}.${childIndex}.audios[${audioIndex}].audio`,
        undefined
      );
      formik.setFieldError(
        `${parentPath}.${childIndex}.audios[${audioIndex}].audio`,
        SystemMessage.MAX_SIZE_UPLOAD
      );

      return;
    }

    try {
      const response = await UploadMediaService.upload({ medias: [file] });
      const uploadedUrl = response.data.items[0].url;

      formik.setFieldValue(
        `${parentPath}.${childIndex}.audios[${audioIndex}].audio`,
        uploadedUrl
      );
    } catch (error) {
      formik.setFieldError(
        `${parentPath}.${childIndex}.audios[${audioIndex}].audio`,
        "Error uploading file"
      );
    }
  };

  const handleThumbnailFileChange = async (
    name: string,
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.currentTarget.files;

    if (!files?.length) {
      formik.setFieldValue(name, undefined);

      return;
    }

    const fileArray: File[] = [];
    const fieldNameForError = name.replace("thumbnails", "filenames");

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      try {
        const dimensions = await getImageDimensions(file);

        if (dimensions.width !== 600 || dimensions.height !== 900) {
          formik.setFieldError(
            fieldNameForError,
            "Kích thước ảnh phải là 600x900px"
          );

          return;
        }
      } catch (error) {
        formik.setFieldError(fieldNameForError, "Không thể đọc kích thước ảnh");

        return;
      }
      fileArray.push(file);
    }

    try {
      const response = await UploadMediaService.upload({ medias: fileArray });
      const uploadedUrls = response.data.items.map((item) => item.url);
      const existingThumbnails = formik.getFieldProps(name).value || [];

      formik.setFieldValue(name, [...existingThumbnails, ...uploadedUrls]);
      formik.setFieldError(fieldNameForError, undefined);
    } catch (error) {
      formik.setFieldError(name, "Error uploading files");
    }
  };

  const handleClickPreviewAudio = (chapterName: string, linkAudio?: string) => {
    if (linkAudio) {
      dispatch(showAudiobookPanel());
      dispatch(sendAudiobook({ chapterName, linkAudio }));
    }
  };

  const handleAccordionChange =
    (index: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedChapters((prev) => ({
        ...prev,
        [index]: isExpanded,
      }));
    };

  const handleDeleteChapter = (index: number, chapterName: string, removeFunction: (index: number) => void) => {
    const confirmModalObs = openPortalDialog(ConfirmModal, {
      message: `Bạn có chắc chắn muốn xóa chương '${chapterName}' không?`,
    });

    confirmModalObs.afterClosed().subscribe((data) => {
      if (data?.isAccept) {
        removeFunction(index);
      }
    });
  };

  return (
    <div>
      <FieldArray name={arrayName}>
        {({ push, remove }) => (
          <div>
            {!!values.length &&
              values.map((element, index) => (
                <div key={index}>
                  <Accordion
                    expanded={expandedChapters[index] || false}
                    onChange={handleAccordionChange(index)}
                    sx={{
                      marginY: 2,
                      boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                      "&:before": {
                        display: "none",
                      },
                      "&.Mui-expanded": {
                        margin: "16px 0",
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#ffffff" : "#f9fafb",
                        "&.Mui-expanded": {
                          minHeight: "64px",
                        },
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          "&.Mui-expanded": {
                            margin: "12px 0",
                          },
                        },
                      }}
                    >
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                            {index + 1}
                          </div>
                          <Typography
                            variant="h6"
                            className="font-semibold text-gray-800"
                          >
                            {element.name || `Chương ${index + 1}`}
                          </Typography>
                        </div>
                        {index !== 0 && (
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteChapter(index, element.name || `Chương ${index + 1}`, remove);
                            }}
                            size="small"
                            className="text-red-500"
                          >
                            <RiCloseLine className="text-[24px]" />
                          </IconButton>
                        )}
                      </div>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        padding: "24px",
                        backgroundColor:
                          index % 2 === 0 ? "#ffffff" : "#f9fafb",
                      }}
                    >
                      {(expandedChapters[index] || false) && (
                        <>
                          {!!values[index].modelConverts?.length && (
                            <div className="flex gap-4 w-full mt-4">
                              <div className="flex-1">
                                <h3 className="font-medium text-[16px] mb-2">
                                  Giọng chuẩn
                                </h3>
                              </div>
                              <div className="flex-1">
                                <h3 className="font-medium text-[16px] mb-2">
                                  Giọng đã huấn luyện
                                </h3>
                              </div>
                              <div className="w-[24px]"></div>
                            </div>
                          )}

                          {(values[index].modelConverts || []).map(
                            (_, modelIndex) => (
                              <div
                                className="flex gap-4 mb-4 items-center"
                                key={`model-convert-${modelIndex}`}
                              >
                                <div className="flex-1 flex gap-1 items-center">
                                  <div className="flex-1">
                                    <FormControl
                                      name={`${arrayName}.${index}.modelConverts[${modelIndex}].endpoint`}
                                    >
                                      <CustomReactSelect
                                        options={endpointConversions ?? []}
                                        placeholder="Các giọng đọc AI phổ biến"
                                        isClearable
                                        isDisabled={
                                          !!values[index].modelConverts?.[
                                            modelIndex
                                          ]?.model?.value
                                        }
                                      />
                                    </FormControl>
                                  </div>
                                  {values[index].modelConverts[modelIndex]
                                    .endpoint?.value && (
                                    <div className="flex items-center">
                                      <div>
                                        <abbr title="Nghe audio">
                                          <img
                                            className="w-8 cursor-pointer"
                                            src={Images.PlayPreviewIcon.default}
                                            alt=""
                                            onClick={() => {
                                              const selectedEndpointValue =
                                                  values[index].modelConverts?.[
                                                    modelIndex
                                                  ]?.endpoint?.value;
                                              const selectedEndpoint =
                                                  endpointConversions?.find(
                                                    (ep) =>
                                                      ep.value ==
                                                      selectedEndpointValue
                                                  );
                                              const audioLink =
                                                  selectedEndpoint?.linkAudio;

                                              handleClickPreviewAudio(
                                                values[index].name,
                                                  audioLink
                                                    ? s3Url + audioLink
                                                    : undefined
                                              );
                                            }}
                                          />
                                        </abbr>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 flex gap-1 items-center">
                                  <div className="flex-1 flex gap-1 items-center">
                                    <div className="flex-1">
                                      <FormControl
                                        name={`${arrayName}.${index}.modelConverts[${modelIndex}].model`}
                                      >
                                        <CustomReactSelect
                                          options={modelTrains ?? []}
                                          placeholder="Các giọng đọc đã tùy chỉnh"
                                          isClearable
                                          isDisabled={
                                            !!values[index].modelConverts?.[
                                              modelIndex
                                            ]?.endpoint?.value
                                          }
                                        />
                                      </FormControl>
                                    </div>
                                    {values[index].modelConverts[modelIndex]
                                      .model?.value && (
                                      <div className="flex items-center">
                                        <div>
                                          <abbr title="Nghe audio">
                                            <img
                                              className="w-8 cursor-pointer"
                                              src={
                                                Images.PlayPreviewIcon.default
                                              }
                                              alt=""
                                              onClick={() =>
                                                handleClickPreviewAudio(
                                                  values[index].name,
                                                  s3Url +
                                                    (
                                                      values[index]
                                                        .modelConverts?.[
                                                          modelIndex
                                                        ]?.model as any
                                                    )?.linkAudio
                                                )
                                              }
                                            />
                                          </abbr>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div
                                  className="cursor-pointer"
                                  onClick={() => {
                                    const newModelConverts = [
                                      ...(values[index].modelConverts || []),
                                    ];

                                    newModelConverts.splice(modelIndex, 1);
                                    formik.setFieldValue(
                                      `${arrayName}.${index}.modelConverts`,
                                      newModelConverts
                                    );
                                  }}
                                >
                                  <RiCloseLine className="text-[#dc3545] text-[24px] font-semibold" />
                                </div>
                              </div>
                            )
                          )}
                          {canCreateAudio && (
                            <div className="mb-4">
                              <Button
                                type="button"
                                label="Thêm giọng đọc AI tự động từ văn bản"
                                width={350}
                                className="h-[52px] text-[#5570F1] rounded-lg border-[2px] border-solid border-[#5570F1] hover:bg-[#c3cdff]"
                                labelClassName="font-medium pl-4"
                                theme="none"
                                icon={Images.Plus2Icon.default}
                                onClick={() => {
                                  const currentModelConverts =
                                    values[index].modelConverts || [];
                                  const newModelConverts = [
                                    ...currentModelConverts,
                                    {
                                      endpoint: { value: "", label: "" },
                                      model: { value: "", label: "" },
                                    },
                                  ];

                                  formik.setFieldValue(
                                    `${arrayName}.${index}.modelConverts`,
                                    newModelConverts
                                  );
                                }}
                              />
                              <p className="text-[12px] text-[#dc3545] mt-2">
                                Vui lòng chỉ chọn giọng chuẩn hoặc giọng đã
                                huấn luyện
                              </p>
                            </div>
                          )}

                          {/* Audio */}
                          {(values[index].audios || []).map(
                            (audio, audioIndex) => (
                              <div
                                className="flex gap-4 mt-3 mb-4 items-center"
                                key={audioIndex}
                              >
                                <div className="flex-1">
                                  <FormControl
                                    name={`${arrayName}.${index}.audios[${audioIndex}].name`}
                                  >
                                    <Input
                                      placeholder="Tên audio"
                                      disabled={!canUploadAudio}
                                      value={audio.name}
                                    />
                                  </FormControl>
                                </div>
                                <div className="flex-1 flex gap-1 items-center">
                                  <FormControl
                                    name={`${arrayName}.${index}.audios[${audioIndex}].audio`}
                                  >
                                    <Input
                                      placeholder="Audio"
                                      inputClassName="!pl-4 !pr-10"
                                      inputIcon={
                                        canUploadAudio
                                          ? Images.LinkIcon.default
                                          : undefined
                                      }
                                      inputIconClassName={
                                        canUploadAudio
                                          ? "!right-3 !top-[14px] !left-auto !w-6 !h-6 cursor-pointer hover:bg-gray-300"
                                          : undefined
                                      }
                                      onIconClick={
                                        canUploadAudio
                                          ? () =>
                                              onAddAudioChapterClick(
                                                index,
                                                audioIndex
                                              )
                                          : undefined
                                      }
                                      disabled
                                      value={
                                        values[index].audios[audioIndex]
                                          .audio || ""
                                      }
                                    />
                                  </FormControl>
                                  {!!values[index].audios[audioIndex].audio && (
                                    <div>
                                      <abbr title="Nghe audio">
                                        <img
                                          className="w-8 cursor-pointer"
                                          src={Images.PlayPreviewIcon.default}
                                          alt=""
                                          onClick={() =>
                                            handleClickPreviewAudio(
                                              values[index].name,
                                              s3Url +
                                              values[index].audios[audioIndex]
                                                .audio
                                            )
                                          }
                                        />
                                      </abbr>
                                    </div>
                                  )}
                                </div>
                                {canUploadAudio && (
                                  <>
                                    <div
                                      className="cursor-pointer"
                                      onClick={() => {
                                        const newAudios = [
                                          ...(values[index].audios || []),
                                        ];

                                        newAudios.splice(audioIndex, 1);
                                        formik.setFieldValue(
                                          `${arrayName}.${index}.audios`,
                                          newAudios
                                        );
                                      }}
                                    >
                                      <RiCloseLine className="text-[#dc3545] text-[24px] font-semibold" />
                                    </div>
                                    <input
                                      ref={(el) => {
                                        if (el) {
                                          const refKey = `${index}-${audioIndex}`;

                                          inputAudioChapterRef.current[refKey] =
                                            el;
                                        }
                                      }}
                                      type="file"
                                      hidden
                                      name={`audio-chapter-${index}-${audioIndex}`}
                                      onChange={(e) =>
                                        handleAudioChapterChange(
                                          e,
                                          index,
                                          audioIndex
                                        )
                                      }
                                    />
                                  </>
                                )}
                              </div>
                            )
                          )}

                          {canUploadAudio && (
                            <div className="mb-4">
                              <Button
                                type="button"
                                label="Thêm file Audio có sẵn"
                                width={250}
                                className="h-[52px] text-[#5570F1] rounded-lg border-[2px] border-solid border-[#5570F1] hover:bg-[#c3cdff]"
                                labelClassName="font-medium pl-4"
                                theme="none"
                                icon={Images.Plus2Icon.default}
                                onClick={() => {
                                  const currentAudios =
                                    values[index].audios || [];
                                  const newAudios = [
                                    ...currentAudios,
                                    {
                                      name: "",
                                      audio: "",
                                    },
                                  ];

                                  formik.setFieldValue(
                                    `${arrayName}.${index}.audios`,
                                    newAudios
                                  );
                                }}
                              />
                            </div>
                          )}
                          {isUpdate && (
                            <label htmlFor="">
                              <div className="flex">
                                <FormControl
                                  name={`${arrayName}.${index}.isForceUpdateAudio`}
                                >
                                  <Checkbox
                                    label="Tạo lại audio"
                                    labelClassName="text-sm text-[#83898C]"
                                  />
                                </FormControl>
                              </div>
                            </label>
                          )}
                          <div className="flex gap-4 mt-3">
                            <div className="flex-1">
                              <FormControl name={`${arrayName}.${index}.name`}>
                                <Input placeholder="Tên chương, Tên phần" />
                              </FormControl>
                            </div>
                            <div className="flex-1">
                              <FormControl name={`${arrayName}.${index}.point`}>
                                <Input placeholder="Giá bán lẻ chương này (Ví dụ: 1.000 Kas)" />
                              </FormControl>
                            </div>
                          </div>
                          {/* <label htmlFor="">
                            <div className="mt-3 flex">
                              <FormControl
                                name={`${arrayName}.${index}.isAutoGenerateThumbnail`}
                              >
                                <Checkbox
                                  label="Tạo hình thu nhỏ bằng AI"
                                  labelClassName="text-sm text-[#83898C]"
                                />
                              </FormControl>
                            </div>
                          </label> */}
                          <div className="mt-3">
                            <FormControl
                              name={`${arrayName}.${index}.filenames`}
                            >
                              <FileInput
                                label="Chọn hình ảnh cho chương này (JPG, PNG, tỷ lệ 9:16 - Kích thước 600-900px)"
                                labelClassName="text-sm text-[#83898C]"
                                isMultiple
                                onChange={(e) =>
                                  handleThumbnailFileChange(
                                    `${arrayName}.${index}.thumbnails`,
                                    e
                                  )
                                }
                                key={`file-input-${index}-${values[index].thumbnails?.length || 0}`}
                              />
                            </FormControl>
                          </div>
                          {!!element.thumbnails?.length && (
                            <div className="mt-3">
                              <div className="text-sm text-[#83898C]">
                                Hình ảnh hiện tại
                              </div>
                              <div className="flex gap-2 flex-wrap">
                                {(element.thumbnails || []).map(
                                  (thumbnail, thumbIndex) => (
                                    <div
                                      key={`${thumbnail}-${thumbIndex}`}
                                      className="relative w-32"
                                    >
                                      <img
                                        src={String(s3Url + thumbnail)}
                                        className="w-32"
                                      />
                                      <div
                                        className="absolute top-1 right-1 p-1 bg-white rounded-full cursor-pointer hover:bg-gray-100"
                                        onClick={() => {
                                          const newThumbnails = [
                                            ...(element.thumbnails || []),
                                          ];

                                          newThumbnails.splice(thumbIndex, 1);
                                          formik.setFieldValue(
                                            `${arrayName}.${index}.thumbnails`,
                                            newThumbnails
                                          );
                                        }}
                                      >
                                        <RiCloseLine className="text-[#dc3545] text-[24px] font-semibold" />
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                          <FormControl
                            name={`${arrayName}.${index}.description`}
                          >
                            <TextArea
                              textAreaClassName="bg-color border-none outline-none rounded-lg !px-4 !py-3"
                              className="mt-4"
                              width="100%"
                              height="auto"
                              rows={5}
                              placeholder="Mô tả"
                              isStandardizeContent={true}
                            />
                          </FormControl>
                          <div className="mt-[30px]">
                            <FormControl name={`${arrayName}.${index}.content`}>
                              <CustomEditor
                                placeholder="Nội dung sách"
                                isStandardizeContent={true}
                              />
                            </FormControl>
                          </div>
                          <SubChapter
                            arrayName={arrayName}
                            parentPath={`${arrayName}.${index}.children`}
                            childElements={values[index].children || []}
                            level={0}
                            maxLevel={maxLevel}
                            parentNumber={`${index + 1}`}
                            modelTrains={modelTrains}
                            endpointConversions={endpointConversions}
                            isUpdate={isUpdate}
                            canCreateAudio={canCreateAudio}
                            canUploadAudio={canUploadAudio}
                            onAudioChange={handleAudioSubChapterChange}
                            onThumbnailChange={handleThumbnailFileChange}
                            onPreviewAudio={handleClickPreviewAudio}
                            expandedState={expandedSubChapters}
                            setExpandedState={setExpandedSubChapters}
                          />
                        </>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </div>
              ))}
            <Button
              type="button"
              label="Thêm chương mới"
              width={212}
              className="h-[52px] text-[#5570F1] rounded-lg border-[2px] border-solid border-[#5570F1] hover:bg-[#c3cdff]"
              labelClassName="font-medium pl-4"
              theme="none"
              icon={Images.Plus2Icon.default}
              onClick={() => {
                const newChapterIndex = values.length;
                const newChapter = {
                  name: "",
                  audio: "",
                  description: "",
                  content: "",
                  point: "0",
                  isForceUpdateAudio: false,
                  isAutoGenerateThumbnail: false,
                  filenames: "",
                  thumbnails: [],
                  children: [],
                  modelConverts: [],
                  audios: [],
                };

                push(newChapter);

                setExpandedChapters((prev) => ({
                  ...prev,
                  [newChapterIndex]: true,
                }));
              }}
            />
          </div>
        )}
      </FieldArray>
    </div>
  );
}

export default Chapter;
